// Sends a signed-in B2B buyer's native cart to the portal review screen instead
// of to Shopify checkout.
//
// Why this exists: every other B2B gate in this theme lives on the PDP
// (buy-buttons.liquid, base.css, b2b-price-fill.js, b2b-grid.js). The cart was
// never gated, so a buyer who added anything from the main store got the plain
// retail "Check out" and paid by card at retail — outside their account, their
// PO reference, their delivery address and their invoice, and invisible to the
// order flow they use for everything else.
//
// Only uncontracted lines can be in this cart: a contracted variant's native
// buy button is hidden per-variant, so the cart cannot accumulate a
// contract-priced line by construction (see the pre-paint comment in
// theme.liquid). "Uncontracted" is why the transfer needs /add-to-range at all
// — the portal review screen can only price what is in the buyer's range, so
// these products are added to it AT RETAIL first ({} entry, no discount
// invented) and the buyer pays exactly what the cart said.
//
// The handover itself is the one the storefront order bar already uses: write
// the lines to mgp_b2b_basket, navigate to /apps/portal/?review=1, and let
// seedFromStorefrontBasket in quote-cart-worker/portal.js drain it.
(function () {
  'use strict';
  if (document.documentElement.dataset.b2b !== '1') return;

  var PREFIX = '/apps/portal';
  var BASKET_KEY = 'mgp_b2b_basket';
  var LABEL = 'Review order in your portal';

  function withTok(url) {
    var t;
    try { t = localStorage.getItem('mgp_b2b_stok'); } catch (e) { t = null; }
    if (!t) return url;
    return url + (url.indexOf('?') >= 0 ? '&' : '?') + 'stok=' + encodeURIComponent(t);
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  // One call per distinct product, and the endpoint adds every variant of it at
  // retail. Idempotent: a product already in range answers alreadyInRange
  // without writing. It is rate limited to 20 range changes per 5 minutes per
  // company, so a cart holding more than 20 distinct products fails loudly on
  // the 429 rather than transferring half an order.
  // ponytail: no batching endpoint — carts this size do not happen. Add a bulk
  // /add-to-range if one ever does.
  function addAllToRange(cart) {
    var products = [];
    cart.items.forEach(function (i) {
      var gid = 'gid://shopify/Product/' + i.product_id;
      if (products.indexOf(gid) === -1) products.push(gid);
    });
    return products.reduce(function (chain, gid) {
      return chain.then(function () {
        return fetch(withTok(PREFIX + '/add-to-range'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ product: gid }),
        })
          .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, d: d }; }); })
          .then(function (res) {
            if (!res.ok || !res.d.ok) throw new Error((res.d && res.d.error) || 'could not add that product to your range');
          });
      });
    }, Promise.resolve());
  }

  // Order matters. /add-to-range runs first and is idempotent, so a failure
  // there changes nothing and the cart is still intact to retry. The cart is
  // only cleared once the basket is written, so no line can fall between the
  // two — a failed clear leaves a stale cart, which is visible and harmless,
  // rather than an order that vanished.
  function transfer(button) {
    var original = button.innerHTML;
    button.disabled = true;
    button.textContent = 'Moving your order…';
    getCart()
      .then(function (cart) {
        if (!cart.items.length) throw new Error('Your cart is empty.');
        return addAllToRange(cart).then(function () {
          try {
            localStorage.setItem(
              BASKET_KEY,
              JSON.stringify(cart.items.map(function (i) { return { variantId: 'gid://shopify/ProductVariant/' + i.id, quantity: i.quantity }; }))
            );
          } catch (e) {
            throw new Error('Your browser is blocking storage, so the order could not be moved.');
          }
          return fetch('/cart/clear.js', { method: 'POST' }).catch(function () {});
        });
      })
      .then(function () {
        location.href = withTok(PREFIX + '/?review=1');
      })
      .catch(function (err) {
        button.disabled = false;
        button.innerHTML = original;
        var note = button.parentNode.querySelector('[data-b2b-handoff-err]');
        if (!note) {
          note = document.createElement('p');
          note.setAttribute('data-b2b-handoff-err', '');
          note.setAttribute('role', 'alert');
          note.className = 'b2b-handoff-err';
          button.parentNode.insertBefore(note, button.nextSibling);
        }
        note.textContent = (err && err.message) || 'Your order could not be moved. Try again shortly.';
      });
  }

  // Delegated in the capture phase so it beats the cart form's own submit, and
  // so it covers the drawer and the cart notification, which are rendered after
  // this script runs and re-rendered on every cart change.
  document.addEventListener(
    'click',
    function (e) {
      var button = e.target.closest && e.target.closest('[name="checkout"]');
      if (!button) return;
      e.preventDefault();
      e.stopPropagation();
      transfer(button);
    },
    true
  );

  // Relabel every checkout button the theme renders, whenever it renders one.
  // "Check out" on a trade account promises a card payment that is not what
  // happens next.
  function relabel(root) {
    (root || document).querySelectorAll('[name="checkout"]').forEach(function (b) {
      if (b.dataset.b2bRelabelled) return;
      b.dataset.b2bRelabelled = '1';
      b.textContent = LABEL;
    });
  }
  relabel();
  new MutationObserver(function () { relabel(); }).observe(document.documentElement, { childList: true, subtree: true });
})();
